# WHAT THE THREE SHELLS SHARE — card 6ac022cba664bc34d57be8c3.
#
# /app (app-route.coffee), /teacher (TeacherPortal/app-route.coffee) and /org
# (Organization/app-route.coffee) are three webpack entries. Each used to carry
# its own copy of the route wrapper, the router configuration, the React root
# and the boot-error screen, and the copies drifted (the org shell's fallback
# named a route it does not have). They live here once. Each entry keeps its own
# route list (routes.coffee beside it) and its role-specific boot: how it reads
# a sign-in link, and where it sends each kind of account.
import React, { lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import theme from './theme.coffee'
import { SchemeProvider } from './color_scheme.coffee'
import GlobalToast from './global-toast.coffee'
import ErrorBoundary from './error_boundary.coffee'
import { LoadingScreen as Loading, hide_agent_widget } from './common.coffee'
import { loadChunk } from './lazy_route.coffee'
import { installTransitionRecovery } from './transition_recovery.coffee'
import { installRouteChangeUpgrade } from './upgrade_on_route_change.coffee'
import { createRouter } from './router/hash_router.coffee'
import { RouterView } from './router/router_view.coffee'
import { TranslationProvider } from '../../../../schema/framework/shared/i18n/index.coffee'

# A route's screen: its chunk is fetched WHEN THE ROUTE RENDERS, and a lost
# fetch is retried once or twice before the boundary ever sees it — card
# 6aad9e4dbb8767fbeba89f2c. `factory` is `-> import('./x.coffee')`, not
# `import('./x.coffee')`: evaluating the import here would request every route
# chunk the moment the entry loads (24 parallel requests on /app before the
# login form can render) and would cache a rejection for the life of the page,
# which is how a customer on an Android WebView hit 'check your internet
# connection' at the login form twice in a row and could not get past it. See
# lazy_route.coffee.
export screen = (factory, name) ->
  LazyComponent = lazy(-> loadChunk(factory, name))
  (props) ->
    <Suspense fallback={<Loading/>}>
      <LazyComponent {...props} />
    </Suspense>

# The query can sit before the fragment (/org?access_token=..&uid=..#/dashboard)
# or inside it (/app#/login?access_token=.., GET /login's redirect), so cut the
# fragment off what follows the last '?', never off the URL: a uid that
# swallowed '#/dashboard' installed a session for no login (card 6aa6dbae617b8dc32cc6b4c0).
export parseUrl = ( url = location.href ) ->
  params = {}
  ( ( parts = part.split( "=" ) ) && params[ parts[0] ] = parts[1] for part in ( url.split "?" ).pop().split( "#" )[0].split "&" if url.indexOf( "?" ) != -1 ) && params || {}

# Normalize route by removing student IDs but keeping game IDs, so the i18n
# registry files a game's strings once, not once per child.
export normalizeRoute = (route) ->
  return route unless route?
  # Remove studentId from /play/:gameId/:studentId routes
  # Keep the gameId since each game has different strings
  route.replace(/^(#?\/play\/[0-9a-fA-F]{24})\/[0-9a-fA-F]{24}/, '$1')

# The shell's router, with the three hooks every shell installs:
#
#   - A blank grey screen after an aborted XHR is not an outcome anyone can act
#     on (card 6aab65a36c29c3a6dc3bbbb5): transition_recovery retries, then says so.
#   - EVERY SCREEN'S SAFE POINT (cards 6aad97d6bb8767fbeba89eee,
#     6ab16d75013e5b1a5ad54300): a route change is a moment when nothing is
#     half-done, so a tab open since before a client point picks the new bundle
#     up the next time the person goes anywhere. Never on the sign-in screen,
#     never while a credential is in the query string — the owner's ruling on
#     the second card, kept in the helper.
#   - The i18n registry learns which screen a string was seen on.
#
# `label` names the shell in the upgrade's log line; `contextPrefix` is the
# shell's i18n context ('elfeet/', 'elfeet/teacher/', 'elfeet/org/').
export createShellRouter = (routes, {otherwise, guards, label, contextPrefix}) ->
  router = createRouter routes, {otherwise, guards}
  installTransitionRecovery router
  installRouteChangeUpgrade router, label
  router.onSuccess (opened) ->
    # Strip query parameters from route for cleaner tracking
    routeWithoutQuery = window.location.hash.split('?')[0]
    window.i18nRegistry?.setContext(
      "#{contextPrefix}#{opened.name}",
      normalizeRoute(routeWithoutQuery),
      'elfeet'
    )
  router

# On old iOS the bridge sometimes takes an unnecessarily long time to load, so
# the view listens for its late announcement and re-renders the screen.
appChanged = ->
  hide_agent_widget() if window.app?.isApp?()

# WHICH SHELL FOLLOWS THE DEVICE'S DARK MODE (card 6ac075e0270c80c6c2166c4a):
# only the /app parent/student shell, and only it passes `followsDevice`. The
# teacher and org portals keep the fixed light theme — their SCSS was never
# converted to the --el-dk-* variables, so a dark <html> under them is a dark
# bar over light panels with white text on grey (seen on QA 2026-10-03 as
# "Please Add Students" in white on light grey). Until a card converts a
# portal, it stays light whatever the device says.
export startReact = (router, {followsDevice} = {}) ->
  root = createRoot(document.getElementById("root"))
  tree =
    <>
      <CssBaseline />
      <TranslationProvider route={window.location.hash} app="elfeet">
        <div style={{height: "100%"}}>
          <GlobalToast/>
          <RouterView router={router} onAppChanged={appChanged}/>
        </div>
      </TranslationProvider>
    </>
  root.render(
    <ErrorBoundary>
      {if followsDevice
        <SchemeProvider router={router}>{tree}</SchemeProvider>
      else
        <ThemeProvider theme={theme}>{tree}</ThemeProvider>}
    </ErrorBoundary>
  )

# THE BOOT READ'S ERROR PATH — card 6aab772ced72ee7fceb166f3. Throw the error
# from inside the tree so the ErrorBoundary catches it, logs it and renders the
# apology screen — a screen the person can act on beats a blank one.
class GetAccountError extends React.Component
  componentDidMount: () ->
    window.app.ready()
    throw @props.error

  render: () ->
    <div></div>

export renderReactError = (error) ->
  root = createRoot(document.getElementById("root"))
  root.render(
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GetAccountError error={error} />
      </ThemeProvider>
    </ErrorBoundary>
  )
